import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import WidgetCard from './WidgetCard';
import { useWidgetData } from './useWidgetData';

interface ProblemDoc {
  id: number;
  status: number;
  statusName: string;
  typeName: string;
  sender: string;
  recipient: string;
  viaWarehouse: string | null;
  ageDays: number;
}

interface ProblemResponse {
  days: number;
  counts: Array<{ status: number; statusName: string; count: number }>;
  documents: ProblemDoc[];
}

const ACCENT = '#dc2626';

const ageLabel = (d: number) => (d <= 0 ? 'сегодня' : `${d} дн. назад`);

/** Виджет «Проблемные документы»: Отклонен / Ошибка остатка / Ошибка 1С. Право: home.documents.problem.view (+ .limit) */
const ProblemDocumentsWidget: React.FC = () => {
  const { data, loading, error, reload } = useWidgetData<ProblemResponse>('/api/v2/home/documents/problem');
  const docs = data?.documents || [];

  return (
    <WidgetCard
      title="Проблемные документы"
      icon={<ReportProblemOutlinedIcon />}
      accent={ACCENT}
      caption={data ? `за ${data.days} дней` : undefined}
      loading={loading}
      error={error}
      onRefresh={reload}
    >
      {data && (
        <Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, px: 2, py: 1.25 }}>
            {data.counts.map(c => (
              <Chip
                key={c.status} size="small" label={`${c.statusName}: ${c.count}`}
                sx={{ height: 24, fontWeight: 600, bgcolor: c.count ? '#fef2f2' : '#f1f5f9', color: c.count ? '#b91c1c' : '#94a3b8' }}
              />
            ))}
          </Box>
          {docs.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.5, color: '#64748b', borderTop: '1px solid #f1f5f9' }}>
              <TaskAltRoundedIcon sx={{ color: '#10b981' }} />
              <Typography sx={{ fontSize: 13 }}>Проблемных документов нет</Typography>
            </Box>
          ) : (
            docs.map(d => (
              <Box key={d.id} sx={{ px: 2, py: 0.9, borderTop: '1px solid #f1f5f9', '&:hover': { bgcolor: '#f8fafc' } }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap' }}>№ {d.id}</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: ACCENT, whiteSpace: 'nowrap' }}>{d.statusName}</Typography>
                  <Box sx={{ flex: 1 }} />
                  <Typography sx={{ fontSize: 11.5, color: '#94a3b8', whiteSpace: 'nowrap' }}>{ageLabel(d.ageDays)}</Typography>
                </Box>
                <Typography sx={{ fontSize: 12.5, color: '#334155', lineHeight: 1.4 }} noWrap>
                  {d.sender || '-'} → {d.recipient || '-'}
                  {d.viaWarehouse && <Typography component="span" sx={{ fontSize: 12, color: '#94a3b8' }}> (через {d.viaWarehouse})</Typography>}
                </Typography>
              </Box>
            ))
          )}
        </Box>
      )}
    </WidgetCard>
  );
};

export default ProblemDocumentsWidget;
